// ─── Templates ─────────────────────────────────────────────────────────────────
/** A rectangle `rows` tall × `cols` wide, anchored at the top-left. */
export function rectTemplate(rows, cols) {
    const cells = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            cells.push({ r, c });
        }
    }
    return cells;
}
/** The single L (corner) piece this module supports — proof-of-concept non-rect. */
export const L_TEMPLATE = [
    { r: 0, c: 0 },
    { r: 1, c: 0 },
    { r: 1, c: 1 },
];
/**
 * Named shape templates. Ids mirror `generation.json` → `shapes.families`.
 * Rectangle ids read as `rows x cols` (a horizontal 1×2 bar is 1 row × 2 cols).
 * Placement rotation is a later WP; these are the canonical (un-rotated) forms.
 */
export const SHAPE_TEMPLATES = {
    '1x2': rectTemplate(1, 2),
    '1x3': rectTemplate(1, 3),
    '1x4': rectTemplate(1, 4),
    '2x2': rectTemplate(2, 2),
    '2x3': rectTemplate(2, 3),
    L: L_TEMPLATE,
};
// ─── Derivation ────────────────────────────────────────────────────────────────
/**
 * The block's cell-set template. Prefers the explicit `cells`; otherwise derives
 * the legacy rectangle from `orientation`+`size` so existing levels (which carry
 * no `cells`) are represented identically.
 */
export function templateOf(block) {
    if (block.cells && block.cells.length > 0)
        return block.cells;
    const cells = [];
    for (let i = 0; i < block.size; i++) {
        cells.push(block.orientation === 'horizontal' ? { r: 0, c: i } : { r: i, c: 0 });
    }
    return cells;
}
/** Absolute grid cells the block occupies. */
export function occupiedCells(block) {
    return templateOf(block).map((o) => ({
        row: block.position.row + o.r,
        col: block.position.col + o.c,
    }));
}
const key = (c) => `${c.row},${c.col}`;
/** A `"row,col"` key set of the block's occupied cells (for O(1) collision). */
export function occupiedKeySet(block) {
    return new Set(occupiedCells(block).map(key));
}
/** Bounding box of the block's occupied cells (absolute grid coordinates). */
export function boundingBox(block) {
    const cells = occupiedCells(block);
    let minRow = Infinity;
    let minCol = Infinity;
    let maxRow = -Infinity;
    let maxCol = -Infinity;
    for (const { row, col } of cells) {
        if (row < minRow)
            minRow = row;
        if (col < minCol)
            minCol = col;
        if (row > maxRow)
            maxRow = row;
        if (col > maxCol)
            maxCol = col;
    }
    return {
        minRow,
        minCol,
        maxRow,
        maxCol,
        height: maxRow - minRow + 1,
        width: maxCol - minCol + 1,
    };
}
/**
 * The leading-edge cells in a direction — the cell furthest along the direction
 * of travel in each lane perpendicular to it. These are the cells that first meet
 * a wall or a neighbour when the block slides that way.
 *
 * No longer used by the exit rule: Module 53 restated `exitsThroughGate` over the
 * block's own occupied cells, because requiring every lane's leading cell to sit
 * at the wall at once is unsatisfiable for a concave shape. This remains the
 * movement primitive it always was.
 */
export function facingEdgeCells(block, dir) {
    const cells = occupiedCells(block);
    // Group by the perpendicular axis, keep the extreme cell along the travel axis.
    const horizontal = dir === 'left' || dir === 'right';
    const laneOf = (c) => (horizontal ? c.row : c.col);
    const travelOf = (c) => (horizontal ? c.col : c.row);
    const wantMax = dir === 'right' || dir === 'down';
    const best = new Map();
    for (const c of cells) {
        const lane = laneOf(c);
        const cur = best.get(lane);
        if (!cur || (wantMax ? travelOf(c) > travelOf(cur) : travelOf(c) < travelOf(cur))) {
            best.set(lane, c);
        }
    }
    return [...best.values()];
}
/**
 * Greedily decompose a shape template into maximal rectangles (top-left first,
 * grow width then height). A true rectangle → one rect; the L → two. Used by the
 * renderer to draw any shape as glossy tiles without per-cell seams. Offsets are
 * anchor-relative (same frame as `CellOffset`).
 */
export function decomposeToRects(template) {
    const remaining = new Set(template.map((o) => `${o.r},${o.c}`));
    const has = (r, c) => remaining.has(`${r},${c}`);
    const rects = [];
    // Deterministic order: topmost, then leftmost.
    const ordered = [...template].sort((a, b) => a.r - b.r || a.c - b.c);
    for (const start of ordered) {
        if (!has(start.r, start.c))
            continue;
        // Grow width along the row while cells remain.
        let w = 1;
        while (has(start.r, start.c + w))
            w++;
        // Grow height while the full width-row below is still available.
        let h = 1;
        for (;;) {
            let rowOk = true;
            for (let c = 0; c < w; c++) {
                if (!has(start.r + h, start.c + c)) {
                    rowOk = false;
                    break;
                }
            }
            if (!rowOk)
                break;
            h++;
        }
        for (let dr = 0; dr < h; dr++)
            for (let dc = 0; dc < w; dc++)
                remaining.delete(`${start.r + dr},${start.c + dc}`);
        rects.push({ r: start.r, c: start.c, h, w });
    }
    return rects;
}
/** The block's movement constraint, defaulting to `'free'` (4-way). */
export function movementConstraintOf(block) {
    return block.movementConstraint ?? 'free';
}
/** Whether the block's constraint permits sliding in `dir` (axis-lock aware). */
export function canMoveInDirection(block, dir) {
    switch (movementConstraintOf(block)) {
        case 'horizontalOnly':
            return dir === 'left' || dir === 'right';
        case 'verticalOnly':
            return dir === 'up' || dir === 'down';
        case 'free':
        default:
            return true;
    }
}
